import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSession } from "@/auth/session";
import Badge from "@/components/base/Badge";
import {
  createMeterReading,
  getReadingPeriodOverview,
  listReadingPeriods,
  updateMeterReading,
  type ReadingPeriodItem,
  type ReadingPeriodOverview,
  type ReadingPeriodOverviewItem,
} from "@/lib/api";

const emptyOverview: ReadingPeriodOverview = {
  period: null,
  stats: {
    total: 0,
    registered: 0,
    pending: 0,
    anomalies: 0,
    averageConsumptionM3: 0,
    completionPct: 0,
  },
  filters: {
    sectors: [],
    technicians: [],
  },
  rows: [],
};

type EditDraft = {
  readingId: string;
  currentReading: string;
  readAt: string;
  technicianName: string;
  readingStatus: string;
  notes: string;
};

type RegisterDraft = {
  contractId: string;
  readingPeriodId: string;
  previousReading: number;
  currentReading: string;
  readAt: string;
  technicianName: string;
  notes: string;
};

const estadoVariant: Record<ReadingPeriodOverviewItem["status"], "success" | "warning" | "error" | "neutral"> = {
  registrada: "success",
  pendiente: "warning",
  anomalia: "error",
};

const estadoLabel: Record<ReadingPeriodOverviewItem["status"], string> = {
  registrada: "Registrada",
  pendiente: "Pendiente",
  anomalia: "Anomalía",
};

export default function LecturasPeriodoPage() {
  const { session } = useSession();
  const [searchParams] = useSearchParams();
  const [periods, setPeriods] = useState<ReadingPeriodItem[]>([]);
  const [overview, setOverview] = useState<ReadingPeriodOverview>(emptyOverview);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [search, setSearch] = useState("");
  const [filterSector, setFilterSector] = useState("Todos");
  const [filterEstado, setFilterEstado] = useState<"todos" | ReadingPeriodOverviewItem["status"]>("todos");
  const [filterTecnico, setFilterTecnico] = useState("todos");
  const [lecturaExpandida, setLecturaExpandida] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<ReadingPeriodOverviewItem | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [registeringRow, setRegisteringRow] = useState<ReadingPeriodOverviewItem | null>(null);
  const [registerDraft, setRegisterDraft] = useState<RegisterDraft | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingRegister, setSavingRegister] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const requestedPeriodId = searchParams.get("readingPeriodId") ?? "";
  const requestedEstado = searchParams.get("estado");

  useEffect(() => {
    if (!session) return;
    let cancelled = false;

    async function loadPeriods(): Promise<void> {
      setLoading(true);
      setError("");
      try {
        const items = await listReadingPeriods(session);
        if (cancelled) return;
        setPeriods(items);
        const defaultPeriod =
          items.find((item) => item.id === requestedPeriodId) ??
          items.find((item) => item.status === "open") ??
          items[0];
        if (defaultPeriod) {
          setSelectedPeriodId((current) => current || defaultPeriod.id);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "No se pudieron cargar los períodos");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadPeriods();
    return () => {
      cancelled = true;
    };
  }, [requestedPeriodId, session]);

  useEffect(() => {
    if (requestedEstado === "pendiente" || requestedEstado === "registrada" || requestedEstado === "anomalia") {
      setFilterEstado(requestedEstado);
      return;
    }
    if (requestedEstado === "todos") {
      setFilterEstado("todos");
    }
  }, [requestedEstado]);

  async function loadOverview(periodId: string): Promise<void> {
    if (!session) return;
    setLoading(true);
    setError("");
    try {
      const nextOverview = await getReadingPeriodOverview(session, periodId);
      setOverview(nextOverview);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "No se pudieron cargar las lecturas del período");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!selectedPeriodId) return;
    void loadOverview(selectedPeriodId);
  }, [selectedPeriodId]);

  const sectorOptions = useMemo(() => ["Todos", ...overview.filters.sectors], [overview.filters.sectors]);
  const technicianOptions = useMemo(() => overview.filters.technicians.filter(Boolean), [overview.filters.technicians]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return overview.rows.filter((item) => {
      const matchesSearch =
        term.length === 0 ||
        item.customerName.toLowerCase().includes(term) ||
        item.meterSerialNumber.toLowerCase().includes(term) ||
        item.routeName.toLowerCase().includes(term) ||
        item.contractNumber.toLowerCase().includes(term);
      const matchesSector = filterSector === "Todos" || item.sectorName === filterSector;
      const matchesEstado = filterEstado === "todos" || item.status === filterEstado;
      const matchesTecnico = filterTecnico === "todos" || item.technicianName === filterTecnico;
      return matchesSearch && matchesSector && matchesEstado && matchesTecnico;
    });
  }, [filterEstado, filterSector, filterTecnico, overview.rows, search]);

  function openEditModal(item: ReadingPeriodOverviewItem): void {
    if (!item.readingId) {
      setError("Solo se pueden editar lecturas ya registradas.");
      return;
    }
    setError("");
    setSuccess("");
    setEditingRow(item);
    setEditDraft({
      readingId: item.readingId,
      currentReading: item.currentReading == null ? "" : String(item.currentReading),
      readAt: toInputDate(item.readAt),
      technicianName: item.technicianName ?? "",
      readingStatus: item.status === "anomalia" ? "anomaly" : "recorded",
      notes: item.notes ?? "",
    });
  }

  function closeEditModal(): void {
    setEditingRow(null);
    setEditDraft(null);
  }

  function openRegisterModal(item: ReadingPeriodOverviewItem): void {
    setError("");
    setSuccess("");
    setRegisteringRow(item);
    setRegisterDraft({
      contractId: item.contractId,
      readingPeriodId: selectedPeriodId,
      previousReading: item.previousReading,
      currentReading: "",
      readAt: todayLocal(),
      technicianName: "",
      notes: "",
    });
  }

  function closeRegisterModal(): void {
    setRegisteringRow(null);
    setRegisterDraft(null);
  }

  async function handleSaveRegister(): Promise<void> {
    if (!session || !registerDraft || !registeringRow) return;
    const currentReading = Number(registerDraft.currentReading);
    if (!Number.isFinite(currentReading) || registerDraft.currentReading === "") {
      setError("La lectura actual debe ser numérica.");
      return;
    }
    if (!registerDraft.readAt) {
      setError("La fecha de lectura es obligatoria.");
      return;
    }
    if (!registerDraft.technicianName.trim()) {
      setError("El técnico es obligatorio.");
      return;
    }
    if (!registerDraft.notes.trim()) {
      setError("La observación es obligatoria.");
      return;
    }
    setSavingRegister(true);
    setError("");
    setSuccess("");
    try {
      await createMeterReading(session, {
        readingPeriodId: registerDraft.readingPeriodId,
        contractId: registerDraft.contractId,
        currentReading,
        readAt: `${registerDraft.readAt}T12:00:00.000Z`,
        readBy: registerDraft.technicianName.trim(),
        readingStatus: "recorded",
        notes: registerDraft.notes.trim(),
      });
      await loadOverview(selectedPeriodId);
      closeRegisterModal();
      setSuccess("Lectura registrada correctamente.");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "No se pudo registrar la lectura");
    } finally {
      setSavingRegister(false);
    }
  }

  async function handleSaveEdit(): Promise<void> {
    if (!session || !editDraft || !selectedPeriodId || !editingRow) return;
    const currentReading = Number(editDraft.currentReading);
    if (!Number.isFinite(currentReading)) {
      setError("La lectura actual debe ser numérica.");
      return;
    }
    if (!editDraft.readAt) {
      setError("La fecha de lectura es obligatoria.");
      return;
    }
    if (!editDraft.technicianName.trim()) {
      setError("El técnico es obligatorio.");
      return;
    }
    if (!editDraft.notes.trim()) {
      setError("La observación es obligatoria.");
      return;
    }

    setSavingEdit(true);
    setError("");
    setSuccess("");
    try {
      await updateMeterReading(session, editDraft.readingId, {
        currentReading,
        readAt: `${editDraft.readAt}T12:00:00.000Z`,
        readBy: editDraft.technicianName.trim() || undefined,
        readingStatus: editDraft.readingStatus,
        notes: editDraft.notes.trim() || null,
      });
      await loadOverview(selectedPeriodId);
      closeEditModal();
      setSuccess("Lectura actualizada correctamente.");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "No se pudo actualizar la lectura");
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleExport(): Promise<void> {
    setExporting(true);
    setError("");
    try {
      const XLSX = await import("xlsx");
      const rows = filtered.map((item) => ({
        medidor: item.meterSerialNumber,
        cliente: item.customerName,
        contrato: item.contractNumber,
        sector: item.sectorName,
        ruta: item.routeName,
        lecturaAnterior: item.previousReading,
        lecturaActual: item.currentReading ?? "",
        consumoM3: item.consumptionM3 ?? "",
        fecha: item.readAt ? formatDate(item.readAt) : "",
        tecnico: item.technicianName ?? item.assignedDeviceCode ?? "",
        estado: estadoLabel[item.status],
        observacion: item.notes ?? item.observation ?? "",
        direccion: item.address,
      }));
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Lecturas");
      const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const filename = `lecturas_periodo_${overview.period?.periodCode ?? "periodo"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      triggerDownload(filename, blob);
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : "No se pudo exportar Excel");
    } finally {
      setExporting(false);
    }
  }

  const modalConsumption = useMemo(() => {
    if (!editingRow || !editDraft) return 0;
    const currentReading = Number(editDraft.currentReading);
    if (!Number.isFinite(currentReading)) return 0;
    return Math.max(0, currentReading - editingRow.previousReading);
  }, [editDraft, editingRow]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-0.5">
            <span>Reportes</span>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-gray-600">Lecturas del Período</span>
          </div>
          <h1 className="text-xl font-bold text-gray-800">Lecturas del Período</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Visualiza y gestiona las lecturas de medidores sin necesidad de descargar Excel
          </p>
        </div>
        <button
          type="button"
          onClick={() => void handleExport()}
          disabled={exporting || filtered.length === 0}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-download-2-line" />
          </div>
          {exporting ? "Exportando..." : "Exportar Excel"}
        </button>
      </div>

      {error ? <div className="p-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">{error}</div> : null}
      {success ? <div className="p-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg">{success}</div> : null}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: "Total Lecturas", value: overview.stats.total.toString(), color: "text-gray-700", bg: "bg-gray-50", icon: "ri-dashboard-line" },
          { label: "Registradas", value: overview.stats.registered.toString(), color: "text-emerald-600", bg: "bg-emerald-50", icon: "ri-check-double-line" },
          { label: "Pendientes", value: overview.stats.pending.toString(), color: "text-amber-600", bg: "bg-amber-50", icon: "ri-time-line" },
          { label: "Anomalías", value: overview.stats.anomalies.toString(), color: "text-red-600", bg: "bg-red-50", icon: "ri-error-warning-line" },
          { label: "Consumo Promedio", value: `${overview.stats.averageConsumptionM3.toFixed(1)} m³`, color: "text-cyan-600", bg: "bg-cyan-50", icon: "ri-drop-line" },
        ].map((item) => (
          <div key={item.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${item.bg} flex-shrink-0`}>
              <i className={`${item.icon} text-lg ${item.color}`} />
            </div>
            <div>
              <div className="text-xl font-bold text-gray-800">{item.value}</div>
              <div className="text-xs text-gray-500">{item.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-sm font-semibold text-gray-700">Avance del Período</span>
            <span className="text-xs text-gray-400 ml-2">{overview.period?.periodCode ?? "Sin período"}</span>
          </div>
          <span className="text-sm font-bold text-gray-800">
            {overview.stats.registered} / {overview.stats.total} lecturas ({Math.round(overview.stats.completionPct)}%)
          </span>
        </div>
        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${overview.stats.completionPct}%` }} />
        </div>
        <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
          <span>Registradas: {overview.stats.registered}</span>
          <span>Pendientes: {overview.stats.pending}</span>
          <span>Anomalías: {overview.stats.anomalies}</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-52">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-search-line text-sm" />
              </div>
              <input
                type="text"
                placeholder="Buscar por cliente, medidor o ruta..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 transition-colors"
              />
            </div>
            <select
              value={selectedPeriodId}
              onChange={(event) => setSelectedPeriodId(event.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-emerald-400 cursor-pointer text-gray-600"
            >
              {periods.map((period) => (
                <option key={period.id} value={period.id}>
                  {period.periodCode} ({period.status})
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
              {sectorOptions.map((sector) => (
                <button
                  key={sector}
                  type="button"
                  onClick={() => setFilterSector(sector)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                    filterSector === sector ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {sector}
                </button>
              ))}
            </div>

            <div className="w-px h-6 bg-gray-200 mx-1" />

            <div className="flex items-center gap-1">
              {(["todos", "registrada", "pendiente", "anomalia"] as const).map((estado) => (
                <button
                  key={estado}
                  type="button"
                  onClick={() => setFilterEstado(estado)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${
                    filterEstado === estado ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {estado === "todos" ? "Todos" : estadoLabel[estado]}
                </button>
              ))}
            </div>

            <div className="w-px h-6 bg-gray-200 mx-1" />

            <select
              value={filterTecnico}
              onChange={(event) => setFilterTecnico(event.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-emerald-400 cursor-pointer text-gray-600"
            >
              <option value="todos">Todos los técnicos</option>
              {technicianOptions.map((technician) => (
                <option key={technician} value={technician}>
                  {technician}
                </option>
              ))}
            </select>

            <span className="text-xs text-gray-400 ml-auto">{filtered.length} resultados</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Medidor</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sector / Ruta</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Lectura Anterior</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Lectura Actual</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Consumo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Técnico</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-10" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, index) => {
                const isExpanded = lecturaExpandida === item.rowId;
                const isAnomaly = item.status === "anomalia";
                const isPending = item.status === "pendiente";
                return (
                  <Fragment key={item.rowId}>
                    <tr
                      onClick={() => setLecturaExpandida(isExpanded ? null : item.rowId)}
                      className={`border-b border-gray-50 transition-colors cursor-pointer ${
                        isAnomaly ? "bg-red-50/30 hover:bg-red-50/60" : isPending ? "bg-amber-50/20 hover:bg-amber-50/50" : index % 2 === 0 ? "" : "bg-gray-50/30"
                      } hover:bg-gray-100/60`}
                    >
                      <td className="px-4 py-3">
                        <span className={`text-sm font-mono font-semibold ${isAnomaly ? "text-red-700" : isPending ? "text-amber-700" : "text-gray-700"}`}>
                          {item.meterSerialNumber}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.customerName}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-700">{item.sectorName}</div>
                        <div className="text-xs text-gray-400">{item.routeName}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right font-mono">{item.previousReading.toLocaleString("es-CL")}</td>
                      <td className="px-4 py-3 text-sm text-gray-800 text-right font-mono font-semibold">
                        {item.currentReading !== null ? item.currentReading.toLocaleString("es-CL") : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {item.consumptionM3 !== null ? (
                          <span className={`text-sm font-semibold font-mono ${item.consumptionM3 > 30 ? "text-red-600" : item.consumptionM3 > 20 ? "text-gray-800" : "text-emerald-600"}`}>
                            {item.consumptionM3} m³
                          </span>
                        ) : (
                          <span className="text-sm text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.readAt ? formatDate(item.readAt) : <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3">
                        {item.technicianName ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                              {getInitials(item.technicianName)}
                            </div>
                            <span className="text-sm text-gray-600">{item.technicianName}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={estadoVariant[item.status]} size="sm">
                          {estadoLabel[item.status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="w-5 h-5 flex items-center justify-center text-gray-300">
                          <i className={`text-sm transition-transform ${isExpanded ? "ri-arrow-up-s-line rotate-180" : "ri-arrow-down-s-line"}`} />
                        </div>
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr className={isAnomaly ? "bg-red-50/20" : isPending ? "bg-amber-50/10" : "bg-gray-50/50"}>
                        <td colSpan={10} className="px-4 py-3">
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                              <span className="text-xs text-gray-400">ID Lectura</span>
                              <p className="text-sm font-semibold text-gray-700 font-mono">{item.readingId ?? "Pendiente"}</p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Diferencia</span>
                              <p className="text-sm font-semibold text-gray-700 font-mono">
                                {item.currentReading !== null ? `${item.currentReading - item.previousReading} m³` : "—"}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Ruta</span>
                              <p className="text-sm text-gray-600">{item.routeName}</p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Sector</span>
                              <p className="text-sm text-gray-600">{item.sectorName}</p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Contrato</span>
                              <p className="text-sm text-gray-600">{item.contractNumber}</p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Dirección</span>
                              <p className="text-sm text-gray-600">{item.address}</p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Dispositivo</span>
                              <p className="text-sm text-gray-600">{item.assignedDeviceCode ?? "—"}</p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">RUT Cliente</span>
                              <p className="text-sm text-gray-600">{item.customerRut}</p>
                            </div>
                            {item.notes || item.observation ? (
                              <div className="col-span-2 lg:col-span-4">
                                <span className="text-xs text-gray-400">Observación</span>
                                <p className={`text-sm mt-0.5 ${isAnomaly ? "text-red-700 font-medium" : "text-gray-600"}`}>
                                  {item.notes ?? item.observation}
                                </p>
                              </div>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200/60">
                            {item.readingId ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openEditModal(item);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                <div className="w-3.5 h-3.5 flex items-center justify-center">
                                  <i className="ri-edit-line text-xs" />
                                </div>
                                Editar lectura
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openRegisterModal(item);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                              >
                                <div className="w-3.5 h-3.5 flex items-center justify-center">
                                  <i className="ri-add-line text-xs" />
                                </div>
                                Registrar lectura
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-500">
                    {loading ? "Cargando lecturas..." : "No hay lecturas para los filtros aplicados."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">Mostrando {filtered.length} de {overview.rows.length} lecturas</span>
          <span className="text-xs text-gray-400">{overview.period?.status === "open" ? "Período abierto" : "Período cerrado"}</span>
        </div>
      </div>

      {editingRow && editDraft ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-start justify-between gap-3 bg-slate-50 px-6 py-4 border-b border-slate-200">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center">
                  <i className="ri-edit-line" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">Editar Lectura</h2>
                  <p className="text-sm text-slate-500">Modificar los datos de la lectura registrada</p>
                </div>
              </div>
              <button type="button" onClick={closeEditModal} className="text-slate-400 hover:text-slate-600">
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 border border-slate-100 p-4">
                <Info label="Medidor" value={editingRow.meterSerialNumber} />
                <Info label="Cliente" value={editingRow.customerName} />
                <Info label="Sector / Ruta" value={`${editingRow.sectorName} — ${editingRow.routeName}`} />
                <Info label="Lectura anterior" value={String(editingRow.previousReading)} />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Lectura actual *</label>
                <div className="relative">
                  <input
                    value={editDraft.currentReading}
                    onChange={(event) => setEditDraft((current) => (current ? { ...current, currentReading: event.target.value } : current))}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-400"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-emerald-600">
                    {modalConsumption >= 0 ? `+ ${modalConsumption} m³` : ""}
                  </span>
                </div>
                <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-700">
                  Consumo calculado: {modalConsumption} m³ {modalConsumption <= 30 ? "dentro de parámetros normales." : "revisar posible sobreconsumo."}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Fecha de lectura *</label>
                  <input
                    type="date"
                    value={editDraft.readAt}
                    onChange={(event) => setEditDraft((current) => (current ? { ...current, readAt: event.target.value } : current))}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Técnico *</label>
                  <select
                    value={editDraft.technicianName}
                    onChange={(event) => setEditDraft((current) => (current ? { ...current, technicianName: event.target.value } : current))}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-400"
                  >
                    <option value="">Selecciona técnico</option>
                    {technicianOptions.map((technician) => (
                      <option key={technician} value={technician}>
                        {technician}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Estado</label>
                <select
                  value={editDraft.readingStatus}
                  onChange={(event) => setEditDraft((current) => (current ? { ...current, readingStatus: event.target.value } : current))}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-400"
                >
                  <option value="recorded">Registrada</option>
                  <option value="anomaly">Anomalía</option>
                  <option value="pendiente">Pendiente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Observación *</label>
                <textarea
                  value={editDraft.notes}
                  maxLength={500}
                  onChange={(event) => setEditDraft((current) => (current ? { ...current, notes: event.target.value } : current))}
                  placeholder="Agregue notas relevantes sobre esta lectura."
                  className="w-full min-h-28 rounded-xl border border-cyan-300 px-4 py-3 text-sm focus:outline-none focus:border-cyan-500"
                />
                <div className="mt-1 text-right text-xs text-slate-400">{editDraft.notes.length}/500</div>
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-white">
              <button type="button" onClick={closeEditModal} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleSaveEdit()}
                disabled={savingEdit}
                className="inline-flex items-center gap-2 rounded-xl bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-60"
              >
                <i className="ri-save-line" />
                {savingEdit ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {registeringRow && registerDraft ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-start justify-between gap-3 bg-slate-50 px-6 py-4 border-b border-slate-200">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center">
                  <i className="ri-add-circle-line" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">Registrar Lectura</h2>
                  <p className="text-sm text-slate-500">Ingresar lectura pendiente desde el portal</p>
                </div>
              </div>
              <button type="button" onClick={closeRegisterModal} className="text-slate-400 hover:text-slate-600">
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 border border-slate-100 p-4">
                <Info label="Medidor" value={registeringRow.meterSerialNumber} />
                <Info label="Cliente" value={registeringRow.customerName} />
                <Info label="Sector / Ruta" value={`${registeringRow.sectorName} — ${registeringRow.routeName}`} />
                <Info label="Lectura anterior" value={String(registeringRow.previousReading)} />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Lectura actual *</label>
                <div className="relative">
                  <input
                    value={registerDraft.currentReading}
                    onChange={(event) => setRegisterDraft((current) => (current ? { ...current, currentReading: event.target.value } : current))}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-400"
                    placeholder="Ingrese el valor del medidor"
                  />
                  {registerDraft.currentReading !== "" && Number.isFinite(Number(registerDraft.currentReading)) ? (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-emerald-600">
                      + {Math.max(0, Number(registerDraft.currentReading) - registerDraft.previousReading)} m³
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Fecha de lectura *</label>
                  <input
                    type="date"
                    value={registerDraft.readAt}
                    onChange={(event) => setRegisterDraft((current) => (current ? { ...current, readAt: event.target.value } : current))}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Técnico *</label>
                  <select
                    value={registerDraft.technicianName}
                    onChange={(event) => setRegisterDraft((current) => (current ? { ...current, technicianName: event.target.value } : current))}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-400"
                  >
                    <option value="">Selecciona técnico</option>
                    {technicianOptions.map((technician) => (
                      <option key={technician} value={technician}>
                        {technician}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Observación *</label>
                <textarea
                  value={registerDraft.notes}
                  maxLength={500}
                  onChange={(event) => setRegisterDraft((current) => (current ? { ...current, notes: event.target.value } : current))}
                  placeholder="Agregue notas relevantes sobre esta lectura."
                  className="w-full min-h-24 rounded-xl border border-cyan-300 px-4 py-3 text-sm focus:outline-none focus:border-cyan-500"
                />
                <div className="mt-1 text-right text-xs text-slate-400">{registerDraft.notes.length}/500</div>
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-white">
              <button type="button" onClick={closeRegisterModal} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleSaveRegister()}
                disabled={savingRegister}
                className="inline-flex items-center gap-2 rounded-xl bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
              >
                <i className="ri-save-line" />
                {savingRegister ? "Guardando..." : "Registrar lectura"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-sm font-medium text-slate-700">{value}</div>
    </div>
  );
}

function todayLocal(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("es-CL", { dateStyle: "short" }).format(date);
}

function toInputDate(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${date.getUTCDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getInitials(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) return "--";
  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("");
}

function triggerDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
